import { useCallback, useEffect, useRef, useState } from "react";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import {
  AddUserRequest,
  FileMap,
} from "../../../app/models/requestModels/userRequest";
import * as Yup from "yup";
import { toast } from "react-toastify";
import getAxiosError from "../../../app/api/axiosError";
import { Form, Formik } from "formik";
import SubmitModalWithFormik from "../../../components/common/modalPopup/SubmitModalWithFormik";
import { Autocomplete, Box, Grid, TextField } from "@mui/material";
import { addUser } from "../../../app/features/user/userSlice";
import FormikTextField from "../../../components/common/formikFields/FormikTextField";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import { useSelector } from "react-redux";
import {
  fetchFileTypeForRoles,
  getRoleList,
} from "../../../app/features/role/roleSlice";
import { RoleFileType } from "../../../app/models/responseModels/roleResponse";
import FormikFileInput from "../../../components/common/formikFields/FormikFileInput";

interface AddUserModalProps {
  closeAddUserModal: CallableFunction;
  onCompleted: CallableFunction;
  requestingFdpId?: number;
  isForFdps?: boolean;
}

function AddUserFormModel(
  firstname: string,
  lastname: string,
  email: string,
  phone: string,
  userActionOwnerType: string,
  deleted: string,
  roles: string[],
  parcelCenterIds: string[],
  files?: FileMap
): AddUserRequest {
  return {
    firstname: firstname,
    lastname: lastname,
    email: email,
    phone: phone,
    userActionOwnerType: userActionOwnerType,
    deleted: deleted,
    roles: roles,
    parcelCenterIds: parcelCenterIds,
    files: files,
  };
}
const AddUserModal = ({
  closeAddUserModal,
  onCompleted,
  requestingFdpId,
  isForFdps,
}: AddUserModalProps) => {
  const dispatch = useAppDispatch();
  const {
    isLoading: isRolesLoading,
    roleList,
    roleFileTypes,
  } = useSelector((state: RootState) => state.role);

  // ***** Get All assigned parcel canters *****
  const assignedParcelCenters = useSelector(
    (state: RootState) =>
      state.login?.authResponse?.userSettings?.assignedParcelCenters
  );
  const isFdp = useSelector(
    (state: RootState) => state.login?.authResponse?.isFdp
  );
  const isFdc = useSelector(
    (state: RootState) => state.login?.authResponse?.isFdc
  );
  const fdp = useSelector(
    (state: RootState) => state.login?.authResponse?.fdpDetails
  );
  const [pcSelectedOptions, setPcSelectedOptions] = useState<string[]>([]);
  const [roleSelectedOptions, setRoleSelectedOptions] = useState<string[]>([]);
  const [fileTypeForRole, setFileTypeForRole] = useState<RoleFileType[]>([]);
  const formikRef = useRef<any>(null); // Reference to Formik instance

  // ***** Callbacks *****
  // Get Roles
  const getRoleData = useCallback(async () => {
    try {
      const response = await dispatch(
        getRoleList(isForFdps as boolean)
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "roleList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "roleListException",
      });
    }
  }, [dispatch]);

  const getFileTypesForRole = useCallback(async () => {
    try {
      const response = await dispatch(
        fetchFileTypeForRoles(roleSelectedOptions)
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "fileTypeListException",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fileTypeListException",
      });
    }
  }, [dispatch, roleSelectedOptions]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getRoleData();
    }
    return () => {
      isFunctionRan = false;
    };
  }, [getRoleData]);

  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getFileTypesForRole();
    }
    return () => {
      isFunctionRan = false;
    };
  }, [getFileTypesForRole]);

  useEffect(() => {
    setFileTypeForRole(roleFileTypes);
    if (formikRef.current) {
      // reset the selected/choosen files if roles are changed
      fileTypeForRole.forEach((file) => {
        formikRef.current.setFieldValue(`files.${file.fileType}`, null);
      });
    }
  }, [roleFileTypes]);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.validateForm(); // Trigger validation when `fileTypeForRole` changes
    }
  }, [fileTypeForRole]);

  const initialValues: AddUserRequest = {
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    deleted: "false",
    userActionOwnerType: "",
    roles: [],
    parcelCenterIds: [],
    files: {} as FileMap,
  };
  const EMAIL_PATTERN =
    /^[_A-Za-z0-9-\\+]+(\.[_A-Za-z0-9-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9]+)*(\.[A-Za-z]{2,})$/;
  const fileSizeValidation = (value: any): boolean => {
    if (!value) return true;
    return value.size <= 5 * 1024 * 1024; // 5 MB
  };
  const createValidationSchema = () => {
    const fileValidations = fileTypeForRole.reduce((acc: any, file) => {
      const fileSchema = Yup.mixed()
        .test(
          "fileSize",
          "File size should not exceed 5 MB",
          fileSizeValidation
        )
        .nullable();
      if (file.requiresOnBoarding) {
        acc[file.fileType] = fileSchema.required(
          `${file.fileType} is required`
        );
      } else {
        acc[file.fileType] = fileSchema;
      }
      return acc;
    }, {});
    return Yup.object().shape({
      firstname: Yup.string()
        .required("First name is Required")
        .matches(
          /^[^\s].*[^\s]$/,
          "First name can't contain extra blank spaces"
        ),
      lastname: Yup.string().matches(
        /^[^\s].*[^\s]$/,
        "Last name can't contain extra blank spaces"
      ),
      email: Yup.string()
        .required("Email is Required")
        .matches(EMAIL_PATTERN, "Invalid Email Format"),
      files: Yup.object().shape(fileValidations),
      roles: Yup.array()
        .required("Role is Required")
        .min(1, "Select at least one role"),
      phone: Yup.string()
        .matches(/^[0-9]+$/, "Phone number must contain only digits")
        .max(12, "Phone number cannot exceed 12 digits"),
    });
  };

  const checkForMandatoryFiles = () => {
    const { values } = formikRef.current;
    const submittingFileTypes = Object.entries(values?.files || {})
      .filter(([fileType, file]) => file)
      .map(([fileType, file]) => fileType);
    const unProvidedMandatoryFileTypes = fileTypeForRole
      .filter(
        (file) =>
          file.isMandatory && !submittingFileTypes.includes(file.fileType)
      )
      .map((file) => file.fileType);
    if (unProvidedMandatoryFileTypes.length > 0) {
      const fileNames = unProvidedMandatoryFileTypes.join(", ");
      const warningMsg = `You have 21 days to upload ${fileNames} documents for the user ${
        values.firstname + " " + values.lastname
      }.`;
      // Return an object with the title and message for the InfoModal
      return {
        title: "Missing Mandatory Files",
        message: warningMsg,
      };
    }
    return null; // Return null to indicate that no InfoModal is required
  };

  const handleSubmit = async (values: AddUserRequest) => {
    try {
      const addUserDetails = AddUserFormModel(
        values.firstname,
        values.lastname,
        values.email,
        values.phone,
        values.userActionOwnerType,
        values.deleted,
        values.roles,
        values.parcelCenterIds
      );
      const filesFormData = new FormData();
      // Append user details as a JSON string
      filesFormData.append(
        "newCmdUser",
        new Blob([JSON.stringify(addUserDetails)], {
          type: "application/json",
        })
      );
      if (values?.files && Object.keys(values.files).length > 0) {
        Object.entries(values.files).forEach(([fileType, file]) => {
          if (file) {
            filesFormData.append(fileType, file);
          }
        });
      }
      let fdpId = isFdp ? fdp?.id : isFdc ? requestingFdpId : null;
      const response = await dispatch(
        addUser({ formData: filesFormData, fdpId: String(fdpId) })
      ).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "addUserSuccess",
        });
        onCompleted();
      } else {
        toast.error(response.message!, {
          toastId: "addUserError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "addUserException",
      });
    }
    return true;
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={createValidationSchema}
      onSubmit={async (values: AddUserRequest, { setSubmitting }) => {
        values.parcelCenterIds = pcSelectedOptions;
        setSubmitting(true);
        await handleSubmit(values);
        setSubmitting(false);
      }}
    >
      {({ setFieldValue }) => {
        // Handle validation on role change
        const handleRoleChange = (event: any, value: any) => {
          const roles = value.map((x: any) => x.name);
          setFieldValue("roles", roles);
          setRoleSelectedOptions(roles);
        };
        return (
          <SubmitModalWithFormik
            title="Add New User"
            closeSubmitModal={closeAddUserModal}
            childErrorMessage={""}
            allowSubmit={false}
            size="lg"
            isLoading={false}
            submitButtonName="Save"
            closeModalAfterSubmit={true}
            hasInfoPopup={true}
            fetchInfoModalData={checkForMandatoryFiles}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Form>
                <Grid container rowSpacing={2} columnSpacing={2}>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="firstname"
                      label="First Name"
                      name="firstname"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="lastname"
                      label="Last Name"
                      name="lastname"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                    />
                  </Grid>
                </Grid>
                <Grid container rowSpacing={2} columnSpacing={2}>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="email"
                      label="Email"
                      name="email"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <Autocomplete
                      onChange={handleRoleChange}
                      multiple
                      id="roles"
                      size="small"
                      loading={isRolesLoading}
                      options={roleList}
                      getOptionLabel={(option) => option.name}
                      filterSelectedOptions
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          margin="normal"
                          id="role"
                          label="Role"
                          name="role"
                          size="small"
                          fullWidth
                          required
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container rowSpacing={2} columnSpacing={2}>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <Autocomplete
                      onChange={(event, value) =>
                        setPcSelectedOptions(value.map((x) => x.parcelCenterId))
                      }
                      multiple
                      id="parcelCenter"
                      size="small"
                      options={assignedParcelCenters ?? []}
                      getOptionLabel={(option) => option.parcelCenterName}
                      filterSelectedOptions
                      renderInput={(params) => (
                        <FormikTextField
                          variant={undefined}
                          {...params}
                          margin="normal"
                          id="parcelCenter"
                          label="Parcel Center"
                          name="parcelCenter"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="phone"
                      label="Phone"
                      name="phone"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6}>
                  {fileTypeForRole.map((file, index) => (
                    <Box key={index}>
                      <FormikFileInput
                        fieldName={`files.${file.fileType}`}
                        label={file.fileType}
                        isMandatory={file.requiresOnBoarding}
                        acceptFormats=".csv, .xls, .xlsx, .pdf, image/jpeg, image/png"
                        sx={{ m: 1 }}
                      />
                    </Box>
                  ))}
                </Grid>
                {/* <Grid item xs={12} sm={12} md={12} lg={12}>
                <Typography sx={{ color: "red" }}>
                  Note: Choosing an <b>"Out of Area"</b> parcel center will
                  result in the assignment of all the parcel centers.
                </Typography>
              </Grid> */}
              </Form>
            </Box>
          </SubmitModalWithFormik>
        );
      }}
    </Formik>
  );
};

export default AddUserModal;
